// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { isVmiAdmin, isVmiUser } from "@insite/client-framework/Store/Data/VmiLocations/VmiLocationsSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { HiddenProps } from "@insite/mobius/Hidden";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LineContainer, { LineHeader, lineStyle } from "@monsma/App/Components/LineContainer/LineContainer";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const settings = getSettingsCollection(state);
    // XNG-Changes
    const location = getLocation(state);
    const disableLazyLoad = parseQueryString<{ disableLazyLoad?: string }>(location.search).disableLazyLoad;
    return {
        wishListSettings: settings.wishListSettings,
        enableVat: settings.productSettings.enableVat,
        vatPriceDisplay: settings.productSettings.vatPriceDisplay,
        vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,
        canViewVmiOrder: isVmiAdmin(settings.orderSettings, session) || isVmiUser(settings.orderSettings, session),
        // XNG-Changes
        showPricing: state.context.showPricing,
        disableLazyLoad: disableLazyLoad?.toLowerCase() === "true",
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: ButtonPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    productInfoVmiBinNumberGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    vmiBinNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
}

export const orderLinesListStyles: OrderLinesListStyles = {
    gridContainer: {
        gap: 10,
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-top: 30px;
            margin-bottom: 0.5rem;
        `,
    },
    orderLineCardContainer: {
        gap: 10,
    },
    orderLineCardGridItem: {
        width: 12,
    },
    orderLineCardImageGridItem: {
        width: 2,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
                padding: 15px 0 0 0;
            }
        `,
    },
    orderLineCardInfoGridItem: {
        width: [8, 8, 7, 8, 8],
        printWidth: 10,
    },
    orderLineCardInfoGridContainer: {
        gap: 10,
    },
    orderLineCardAddToListGridItem: {
        width: [2, 2, 3, 2, 2],
        printWidth: 0,
    },
    orderLineCardActionsWide: {
        above: "sm",
    },
    orderLineCardActionsNarrow: {
        below: "md",
    },
    productInfoSectionOptionsGridItem: {
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
    },
    productInfoGridItem: {
        width: [12, 12, 12, 5, 5],
        printWidth: 6,
    },
    productInfoGridContainer: {
        gap: 5,
    },
    productInfoBrandDescriptionGridItem: {
        width: 12,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
    },
    productInfoDescriptionGridItem: {
        width: 12,
    },
    productInfoErpNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoManufacturerItemGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoVmiBinNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoCustomerProductGridItem: {
        width: [12, 12, 12, 12, 12],
        printWidth: 12,
    },
    orderLineInfoPromotionGridItem: {
        width: 12,
    },
    orderLineInfoPromotionText: {
        size: 12,
        italic: true,
    },
    orderLineInfoGridItem: {
        width: [12, 12, 12, 7, 7],
        printWidth: 6,
    },
    orderLineInfoGridContainer: {
        gap: 5,
    },
    orderLinePriceGridItem: {
        width: [12, 12, 12, 4, 4],
        css: css`
            flex-direction: column;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    productInfoQtyOrderedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoQtyShippedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoSubtotalGridItem: {
        width: [12, 12, 4, 6, 6],
        printWidth: 4,
        css: css`
            flex-direction: column;
        `,
    },
    orderLineNotes: {
        gridItemStyles: {
            width: 12,
        },
    },
    productInfoTotalsGridItem: {
        width: [12, 12, 12, 8, 8],
    },
    productInfoTotalsGridContainer: {
        gap: 10,
    },
    productInfoSubtotalHeadingAndText: {
        text: {
            weight: "bold",
        },
    },
    vmiBinNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
};

const OrderLineProductInfo = ({
    orderLine,
    styles,
    vmiLocationId,
    canViewVmiOrder = false,
}: {
    orderLine: OrderLineModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
    canViewVmiOrder?: boolean;
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <li {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography {...(styles as any).orderLineLabel}>{option.sectionName}</Typography>
                <Typography {...(styles as any).orderLineText}>{option.optionName}</Typography>
            </li>
        ));
    }

    return (
        <>
            <ProductPartNumbers
                productNumber={orderLine.productErpNumber}
                customerProductNumber={orderLine.customerProductNumber}
                manufacturerItem={orderLine.manufacturerItem}
                showCustomerName={false} // xng-change: hide it to match the mock-ups
                showManufacturerItem={false} // xng-change: hide it to match the mock-ups
                extendedStyles={(styles as any).productPartNumbers}
            />
            {orderLine.vmiBinNumber && (
                <GridItem {...styles.productInfoVmiBinNumberGridItem}>
                    <SmallHeadingAndText
                        {...styles.vmiBinNumberHeadingAndText}
                        heading={translate("Bin #")}
                        text={orderLine.vmiBinNumber}
                    />
                </GridItem>
            )}
            {orderLine.brand && <ProductBrand brand={orderLine.brand} extendedStyles={(styles as any).productBrand} />}
            {vmiLocationId && orderLine.isActiveVmiBin && canViewVmiOrder && (
                <VmiBinDetailTypeLink
                    title={orderLine.shortDescription}
                    locationId={vmiLocationId}
                    id={orderLine.vmiBinId}
                />
            )}
            {canViewVmiOrder && vmiLocationId && !orderLine.isActiveVmiBin && (
                <Typography as="p">{orderLine.shortDescription}</Typography>
            )}
            {(!canViewVmiOrder || !vmiLocationId) && (
                <ProductDescription product={orderLine} extendedStyles={styles.productDescriptionStyles} />
            )}
            {sectionOptions.length > 0 && <ul {...styles.productInfoSectionOptionsList}>{sectionOptions}</ul>}
        </>
    );
};

const OrderLineInfoProto = ({
    orderLine,
    order,
    enableVat,
    vatPriceDisplay,
    styles,
}: {
    orderLine: OrderLineModel;
    order: OrderModel;
    enableVat: boolean;
    vatPriceDisplay: string;
    styles: OrderLinesListStyles;
}) => {
    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map(promotion => (
                <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
                    {promotion.name}
                </Typography>
            ));
    }

    return {
        Qty: (
            <>
                <SmallHeadingAndText
                    extendedStyles={styles.productInfoQtyOrderedHeadingAndText}
                    heading={translate("Ordered")}
                    text={`${orderLine.qtyOrdered}`}
                />
                <SmallHeadingAndText
                    extendedStyles={styles.productInfoQtyShippedHeadingAndText}
                    heading={translate("Shipped")}
                    text={`${orderLine.qtyShipped}`}
                />
            </>
        ),
        Price: (
            <>
                <StyledWrapper {...(styles as any).priceWrapper}>
                    <Typography as="p" {...(styles as any).priceHeading}>
                        {translate("Price")}
                    </Typography>
                    <Typography as="p" {...(styles as any).priceText}>
                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                            ? orderLine.unitPriceWithVatDisplay
                            : orderLine.unitPriceDisplay}
                        {orderLine.unitOfMeasure && (
                            <Typography {...(styles as any).priceSubText}>/ {orderLine.unitOfMeasure}</Typography>
                        )}
                    </Typography>
                </StyledWrapper>
                {enableVat && (
                    <>
                        <Typography as="p" {...styles.vatLabelText}>
                            {vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat"
                                ? `${translate("Inc. VAT")} (${orderLine.taxRate}%)`
                                : translate("Ex. VAT")}
                        </Typography>
                        {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                            <>
                                <Typography {...(styles as any).priceText} as="p">
                                    {orderLine.unitPriceDisplay}
                                    <Typography {...(styles as any).priceSubText}>
                                        {orderLine.unitOfMeasure ? `/ ${orderLine.unitOfMeasure} ` : ""}
                                        {translate("Ex. VAT")}
                                    </Typography>
                                </Typography>
                            </>
                        )}
                    </>
                )}
                {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                    <SmallHeadingAndText
                        heading={`${translate("Total")} (${translate("Ex. VAT")})`}
                        text={orderLine.extendedUnitNetPriceDisplay}
                        extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                    />
                )}
                <SmallHeadingAndText
                    heading={
                        !enableVat
                            ? translate("Total")
                            : vatPriceDisplay !== "DisplayWithoutVat"
                              ? `${translate("Total")} (${translate("Inc. VAT")})`
                              : `${translate("Total")} (${translate("Ex. VAT")})`
                    }
                    text={
                        enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                            ? orderLine.netPriceWithVatDisplay
                            : orderLine.extendedUnitNetPriceDisplay
                    }
                    extendedStyles={styles.productInfoSubtotalHeadingAndText}
                />
                {promotions.length > 0 && (
                    <StyledWrapper {...(styles as any).orderLineInfoPromotionWrapper}>
                        <ul {...styles.orderLineInfoPromotionList}>{promotions}</ul>
                    </StyledWrapper>
                )}
            </>
        ),
    };
};

const OrderLineNotes = ({
    orderLine,
    extendedStyles,
    styles,
}: {
    orderLine: OrderLineModel;
    extendedStyles?: OrderLineNotesStyles;
    styles?: OrderLinesListStyles;
}) => {
    return orderLine.notes ? (
        <StyledWrapper {...(extendedStyles as any)?.wrapperItemStyles}>
            <Typography {...(styles as any)?.orderLineLabel}>{translate("Line Notes")}</Typography>
            <Typography {...(styles as any)?.orderLineText}>{orderLine.notes}</Typography>
        </StyledWrapper>
    ) : null;
};

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    canViewVmiOrder?: boolean;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
    enableVat: boolean;
    vatPriceDisplay: string;
    showPricing?: boolean;
    disableLazyLoad: boolean;
}) => {
    const {
        orderLine,
        order,
        wishListSettings,
        vmiBinDetailsPageUrl,
        styles,
        canViewVmiOrder = false,
        setAddToListModalIsOpen,
        addToWishList,
        enableVat,
        vatPriceDisplay,
        showPricing,
        disableLazyLoad,
    } = props;
    const toasterContext = React.useContext(ToasterContext);
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const orderLineInfo = OrderLineInfoProto({
        orderLine,
        order,
        styles,
        enableVat,
        vatPriceDisplay,
    });

    const isShareEntity = getIsShareEntity();

    const hasVmi = orderLine.isActiveProduct && !!order.vmiLocationId && orderLine.isActiveVmiBin;
    const hasProduct = orderLine.isActiveProduct && !!orderLine.productUri;
    const showProductLink = (!canViewVmiOrder && hasProduct) || (canViewVmiOrder && !hasVmi && hasProduct);
    const showVmiLink = canViewVmiOrder && hasVmi;
    const showNoLink = !showProductLink && !showVmiLink;

    return (
        <LineContainer data-test-selector={`orderLineCard${orderLine.productId}`}>
            <LineContainer.Image>
                <StyledWrapper {...(styles as any).imageWrapper}>
                    {showProductLink && (
                        <Clickable {...(styles as any).productImage?.linkWrappingImage} href={orderLine.productUri}>
                            <LazyImage
                                {...styles.orderLineCardImage}
                                src={orderLine.mediumImagePath}
                                isShareEntity={isShareEntity}
                            />
                        </Clickable>
                    )}
                    {showVmiLink && (
                        <Clickable
                            {...(styles as any).productImage?.linkWrappingImage}
                            href={`${vmiBinDetailsPageUrl}?locationId=${order.vmiLocationId}&id=${orderLine.vmiBinId}`}
                        >
                            <LazyImage
                                {...(styles as any).productImage?.image}
                                src={orderLine.mediumImagePath}
                                disableLazyLoad={disableLazyLoad}
                                isShareEntity={isShareEntity}
                            />
                        </Clickable>
                    )}
                    {showNoLink && (
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                        />
                    )}
                </StyledWrapper>
            </LineContainer.Image>
            <LineContainer.Info>
                <OrderLineProductInfo
                    orderLine={orderLine}
                    styles={styles}
                    vmiLocationId={order.vmiLocationId}
                    canViewVmiOrder={canViewVmiOrder}
                />
                {orderLine.canAddToWishlist && (
                    <Link {...(styles as any).addToListLink} onClick={addToListClickHandler}>
                        {translate("Add to List")}
                    </Link>
                )}
            </LineContainer.Info>
            <LineContainer.Qty>{orderLineInfo.Qty}</LineContainer.Qty>
            <LineContainer.Price>{showPricing && orderLineInfo.Price}</LineContainer.Price>
            <LineContainer.Note>
                <OrderLineNotes extendedStyles={styles.orderLineNotes} styles={styles} orderLine={orderLine} />
            </LineContainer.Note>
        </LineContainer>
    );
};

const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    vmiBinDetailsPageUrl,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    enableVat,
    vatPriceDisplay,
    showPricing,
    canViewVmiOrder,
    // xng-change: add disableLazyLoad for the PDF
    disableLazyLoad,
}) => {
    const [styles] = React.useState(() => mergeToNew(mergeToNew(orderLinesListStyles, extendedStyles), lineStyle));

    if (!order || !order.orderLines || !order.orderLines.length) {
        return null;
    }

    // xng-change: override to add the lines header and change some structures tp match the cart lines mock-ups
    return (
        <>
            <Typography {...styles.titleText}>{translate("Products")}</Typography>
            <LineHeader />
            {order.orderLines.map(orderLine => (
                <StyledWrapper key={orderLine.id}>
                    <OrderLineCard
                        orderLine={orderLine}
                        order={order}
                        wishListSettings={wishListSettings}
                        vmiBinDetailsPageUrl={vmiBinDetailsPageUrl}
                        styles={styles}
                        canViewVmiOrder={canViewVmiOrder}
                        setAddToListModalIsOpen={setAddToListModalIsOpen}
                        addToWishList={addToWishList}
                        enableVat={enableVat}
                        vatPriceDisplay={vatPriceDisplay}
                        showPricing={showPricing}
                        disableLazyLoad={disableLazyLoad}
                    />
                </StyledWrapper>
            ))}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
